import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import {
	Box,
	Typography,
	Skeleton,
	CircularProgress,
	IconButton,
	Menu,
	MenuItem,
	ListItemText,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import StatusPill from 'features/IRWorkstation/components/Common/StatusPill';
import FundraisingDrawer from 'features/IRWorkstation/components/Fundraising/FundraisingDrawer';
import FundraisingFilters, {
	INITIAL_FILTERS,
} from 'features/IRWorkstation/components/Fundraising/FundraisingFilters';
import useEntity from 'features/IRWorkstation/hooks/useEntity';
import useFundraisings from 'features/IRWorkstation/hooks/useFundraisings';
import useFundraisingSort from 'features/IRWorkstation/hooks/useFundraisingSort';
import { FUNDRAISING_COLUMNS } from 'features/IRWorkstation/constants/fundraisingColumns';
import salesforceFundraisingsService from 'services/salesforceFundraisingsService';
import { useNotificationContext } from 'contexts/NotificationContext';
import { formatCurrency, formatDate } from '../../../utils/utils';
import ActiveRaiseContent from 'features/IRWorkstation/components/Entity/ActiveRaiseContent';
import useSalesforceRaiseMetrics from 'hooks/useSalesforceRaiseMetrics';

const EntityDetail = ({ onOpenDrawer: _onOpenDrawer }) => {
	const { entityId } = useParams();
	const { showNotification } = useNotificationContext();
	const [filters, setFilters] = useState(INITIAL_FILTERS);
	const { sort, toggleSort } = useFundraisingSort();
	const {
		entity,
		loading: entityLoading,
		error: entityError,
	} = useEntity(entityId);
	const {
		fundraisings,
		totalCount,
		loading: fundraisingsLoading,
		loadingMore,
		error: fundraisingsError,
		loadMore,
		refresh,
	} = useFundraisings(entityId, filters, sort);
	const {
		metrics: raiseMetrics,
		loading: raiseMetricsLoading,
		error: raiseMetricsError,
		refetch: refetchRaiseMetrics,
	} = useSalesforceRaiseMetrics(entityId);

	const [menuAnchorEl, setMenuAnchorEl] = useState(null);
	const [selectedFundraising, setSelectedFundraising] = useState(null);
	const menuOpen = Boolean(menuAnchorEl);

	const [drawerOpen, setDrawerOpen] = useState(false);

	const loading = entityLoading || fundraisingsLoading;
	const error = entityError || fundraisingsError;

	const scrollContainerRef = useRef(null);

	const handleScroll = useCallback(
		(event) => {
			const el = event.currentTarget;

			// loadMore no-ops while a load is in flight or once all records
			// are loaded
			if (el.scrollHeight - el.scrollTop <= el.clientHeight + 50) {
				loadMore();
			}
		},
		[loadMore]
	);

	// Handle opening the action menu
	const handleMenuOpen = (event, fundraising) => {
		event.stopPropagation();
		setMenuAnchorEl(event.currentTarget);
		setSelectedFundraising(fundraising);
	};

	// Handle closing the action menu
	const handleMenuClose = () => {
		setMenuAnchorEl(null);
		setSelectedFundraising(null);
	};

	// Handle edit action from the menu
	const handleMenuEdit = () => {
		if (selectedFundraising) {
			setDrawerOpen(true);
		}
		// Close menu but don't reset selectedFundraising as data is needed for drawer
		setMenuAnchorEl(null);
	};

	// Handle convert to investor action from the menu
	const handleConvert = async () => {
		const fundraisingToConvert = selectedFundraising;
		handleMenuClose();

		if (!fundraisingToConvert) return;

		showNotification('Converting to Investor Commitment...', 'info');

		try {
			await salesforceFundraisingsService.convertFundraising(
				fundraisingToConvert.id
			);
			showNotification(
				'Fundraising converted to Investor Commitment',
				'success'
			);
			refreshData();
		} catch (err) {
			showNotification(
				err?.message || 'Failed to convert fundraising',
				'error'
			);
		}
	};

	const refreshData = () => {
		refresh();
		refetchRaiseMetrics({ showLoading: false });
	};

	const filterByStage = (stage) => {
		// Clear filter if clicking the same stage
		if (filters.stage === stage) {
			setFilters({ ...filters, stage: '' });
			return;
		}
		setFilters({ ...filters, stage });
	};

	// Handle view record in CRM action from the menu. The Lightning URL is built
	// server-side (SalesforceUrlBuilderService) and returned as salesforce_url, so the
	// CRM URL format lives in one place across the app.
	const handleViewInCrm = () => {
		const url = selectedFundraising?.salesforce_url;
		handleMenuClose();

		if (!url) {
			showNotification('Salesforce record URL is unavailable', 'error');
			return;
		}

		window.open(url, '_blank', 'noopener,noreferrer');
	};

	// If the loaded rows don't overflow the container, a scroll event can
	// never fire. Keep loading pages until the content overflows; loadMore
	// no-ops once all records are loaded.
	useEffect(() => {
		const el = scrollContainerRef.current;

		if (el && el.scrollHeight <= el.clientHeight) {
			loadMore();
		}
	}, [fundraisings, loadMore]);

	if (error) {
		return (
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					padding: '20px',
				}}
			>
				<Typography color='error'>Failed to load entity details</Typography>
			</Box>
		);
	}

	// Skeleton loading state
	if (loading) {
		return (
			<Box
				sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
			>
				{/* Header Skeleton */}
				<Box
					sx={{
						padding: '16px 15px',
						backgroundColor: 'background.default',
						display: 'flex',
						alignItems: 'baseline',
						justifyContent: 'space-between',
						gap: '14px',
					}}
					data-testid='skeleton-header'
				>
					<Skeleton variant='text' width={200} height={24} />
					<Skeleton variant='text' width={100} height={16} />
				</Box>

				<Box sx={{ flexShrink: 0 }}>
					<ActiveRaiseContent
						metrics={raiseMetrics}
						loading={raiseMetricsLoading}
						error={raiseMetricsError}
						refetch={refetchRaiseMetrics}
						kpiVariant='default'
						showReupConversion
						onStageClick={filterByStage}
						selectedStage={filters.stage}
					/>
				</Box>

				{/* Table Container Skeleton */}
				<Box
					sx={{
						flex: 1,
						overflowY: 'auto',
						padding: '0 15px 16px 15px',
						backgroundColor: 'background.default',
						display: 'flex',
						flexDirection: 'column',
						gap: '14px',
					}}
				>
					<Box
						sx={{
							backgroundColor: 'background.paper',
							border: '0.5px solid',
							borderColor: 'border.light',
							borderRadius: '12px',
							overflow: 'hidden',
						}}
					>
						{/* Table Header Skeleton */}
						<Box
							sx={{
								padding: '10px 14px',
								borderBottom: '0.5px solid',
								borderColor: 'border.light',
							}}
						>
							<Skeleton variant='text' width={100} height={16} />
						</Box>
						{/* Table Rows Skeleton */}
						<Box sx={{ padding: '10px' }}>
							{[0, 1, 2, 3, 4].map((i) => (
								<Box
									key={i}
									sx={{
										display: 'flex',
										gap: '10px',
										padding: '8px 0',
										borderBottom: '0.5px solid',
										borderColor: 'border.light',
									}}
								>
									<Skeleton variant='text' width={150} height={16} />
									<Skeleton variant='text' width={100} height={16} />
									<Skeleton variant='text' width={100} height={16} />
									<Skeleton variant='text' width={60} height={16} />
									<Skeleton variant='text' width={80} height={16} />
									<Skeleton variant='text' width={100} height={16} />
									<Skeleton variant='text' width={120} height={16} />
									<Skeleton variant='text' width={100} height={16} />
									<Skeleton variant='text' width={80} height={16} />
								</Box>
							))}
						</Box>
					</Box>
				</Box>
			</Box>
		);
	}

	return (
		<Box
			sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
		>
			{/* Header */}
			<Box
				sx={{
					padding: '16px 15px',
					backgroundColor: 'background.default',
					display: 'flex',
					alignItems: 'baseline',
					justifyContent: 'space-between',
					gap: '14px',
				}}
			>
				<Typography sx={{ fontSize: '17px', fontWeight: 700 }}>
					{entity?.name || 'Entity Name'} raise dashboard
				</Typography>
				<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
					{totalCount} total fundraisings
				</Typography>
			</Box>

			<Box sx={{ flexShrink: 0 }}>
				<ActiveRaiseContent
					metrics={raiseMetrics}
					loading={raiseMetricsLoading}
					error={raiseMetricsError}
					refetch={refetchRaiseMetrics}
					kpiVariant='default'
					showReupConversion
					onStageClick={filterByStage}
					selectedStage={filters.stage}
				/>
			</Box>

			{/* Table Container */}
			<Box
				ref={scrollContainerRef}
				onScroll={handleScroll}
				data-testid='fundraisings-scroll-container'
				sx={{
					flex: 1,
					overflowY: 'auto',
					padding: '0 15px 16px 15px',
					backgroundColor: 'background.default',
					display: 'flex',
					flexDirection: 'column',
					gap: '14px',
				}}
			>
				<Box
					sx={{
						backgroundColor: 'background.paper',
						border: '0.5px solid',
						borderColor: 'border.light',
						borderRadius: '12px',
						overflow: 'hidden',
						// Keep full height inside the scrollable container instead of
						// shrinking (which would clip rows) so the container overflows
						// and becomes scrollable.
						flexShrink: 0,
					}}
				>
					{/* Table Header */}
					<Box
						sx={{
							padding: '10px 14px',
							borderBottom: '0.5px solid',
							borderColor: 'border.light',
							display: 'flex',
							alignItems: 'center',
						}}
					>
						<Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
							LP Pipeline
						</Typography>
					</Box>
					{/* Filters */}
					<FundraisingFilters filters={filters} onChange={setFilters} />
					{/* Table */}
					<Box
						component='table'
						sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}
					>
						<Box component='thead'>
							<Box component='tr' sx={{ backgroundColor: 'background.subtle' }}>
								{FUNDRAISING_COLUMNS.map(({ label, sortKey }) => {
									const isActive = sortKey && sort.sortBy === sortKey;

									return (
										<Box
											component='th'
											key={label}
											aria-sort={
												isActive
													? sort.sortDir === 'desc'
														? 'descending'
														: 'ascending'
													: undefined
											}
											sx={{
												textAlign: 'left',
												// A sortable header moves its padding onto the button so the
												// whole cell is the click target, not just the label text
												padding: sortKey ? 0 : '6px 10px',
												fontSize: '12px',
												fontWeight: 700,
												color: isActive ? 'text.primary' : 'text.secondary',
												letterSpacing: '0.05em',
											}}
										>
											{sortKey ? (
												<Box
													component='button'
													type='button'
													onClick={() => toggleSort(sortKey)}
													sx={{
														display: 'flex',
														alignItems: 'center',
														gap: '2px',
														width: '100%',
														padding: '6px 10px',
														border: 'none',
														background: 'none',
														font: 'inherit',
														color: 'inherit',
														letterSpacing: 'inherit',
														textAlign: 'left',
														cursor: 'pointer',
														'&:hover': {
															color: 'text.primary',
															backgroundColor: 'action.hover',
														},
													}}
												>
													{label}
													{isActive &&
														(sort.sortDir === 'desc' ? (
															<ArrowDownwardIcon sx={{ fontSize: '13px' }} />
														) : (
															<ArrowUpwardIcon sx={{ fontSize: '13px' }} />
														))}
												</Box>
											) : (
												label
											)}
										</Box>
									);
								})}
							</Box>
						</Box>
						<Box component='tbody'>
							{fundraisings.map((fundraising) => (
								<Box
									component='tr'
									key={fundraising.id}
									sx={{
										borderBottom: '0.5px solid',
										borderColor: 'border.light',
										'&:hover': { backgroundColor: 'background.subtle' },
									}}
								>
									<Box
										component='td'
										sx={{
											padding: '5px 10px',
											fontWeight: 500,
										}}
									>
										{fundraising.account_name || '-'}
									</Box>
									<Box
										component='td'
										sx={{ padding: '5px 10px', color: 'text.secondary' }}
									>
										{fundraising.investor_type || '-'}
									</Box>
									<Box
										component='td'
										sx={{ padding: '5px 10px', color: 'text.secondary' }}
									>
										{fundraising.internal_coverage || '-'}
									</Box>
									<Box
										component='td'
										sx={{ padding: '5px 10px', color: 'text.secondary' }}
									>
										{fundraising.active ? 'Yes' : 'No'}
									</Box>
									<Box
										component='td'
										sx={{ padding: '5px 10px', color: 'text.secondary' }}
									>
										{fundraising.existing_investor ? 'Yes' : 'No'}
									</Box>
									<Box
										component='td'
										sx={{ padding: '5px 10px', fontWeight: 500 }}
									>
										{formatCurrency(fundraising.commitment) || '-'}
									</Box>
									<Box component='td' sx={{ padding: '5px 10px' }}>
										<StatusPill
											label={fundraising.stage || '-'}
											color='default'
										/>
									</Box>
									<Box
										component='td'
										sx={{ padding: '5px 10px', color: 'text.secondary' }}
									>
										{formatDate(fundraising.anticipated_close_date) || '-'}
									</Box>
									<Box
										component='td'
										sx={{ padding: '5px 10px', color: 'text.secondary' }}
									>
										{fundraising.closing_round || '-'}
									</Box>
									<Box
										component='td'
										sx={{ padding: '5px 10px', color: 'text.secondary' }}
									>
										{formatDate(fundraising.last_interaction_date) || '-'}
									</Box>
									<Box
										component='td'
										sx={{
											padding: '5px 10px',
											color: 'text.secondary',
											display: 'flex',
											justifyContent: 'center',
										}}
									>
										<IconButton
											onClick={(event) => handleMenuOpen(event, fundraising)}
											size='small'
											color='primary'
											aria-label='more options'
											sx={{
												cursor: 'pointer',
												'& *': { cursor: 'pointer !important' },
											}}
										>
											<MoreVertIcon />
										</IconButton>
									</Box>
								</Box>
							))}
							{fundraisings.length === 0 && (
								<Box component='tr'>
									<Box
										component='td'
										colSpan={11}
										sx={{
											padding: '20px',
											textAlign: 'center',
											color: 'text.secondary',
										}}
									>
										No fundraising records found
									</Box>
								</Box>
							)}
						</Box>
					</Box>
				</Box>

				{/* Fundraising Drawer */}
				<FundraisingDrawer
					open={drawerOpen}
					fundraising={selectedFundraising}
					onClose={() => {
						setDrawerOpen(false);
						setSelectedFundraising(null);
					}}
					onSaved={refreshData}
				/>

				{/* Action Menu */}
				<Menu
					anchorEl={menuAnchorEl}
					open={menuOpen}
					onClose={handleMenuClose}
					PaperProps={{
						elevation: 2,
						sx: {
							minWidth: 150,
							maxWidth: 200,
						},
					}}
				>
					<MenuItem onClick={handleMenuEdit}>
						<ListItemText>Edit</ListItemText>
					</MenuItem>
					{selectedFundraising?.active && (
						<MenuItem onClick={handleConvert}>
							<ListItemText>Convert to Investor</ListItemText>
						</MenuItem>
					)}
					<MenuItem onClick={handleViewInCrm}>
						<ListItemText>View record in CRM</ListItemText>
					</MenuItem>
				</Menu>

				{/* Bottom spinner while loading additional pages */}
				{loadingMore && (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
						<CircularProgress size={18} />
					</Box>
				)}
			</Box>
		</Box>
	);
};

EntityDetail.propTypes = {
	onOpenDrawer: PropTypes.func,
};

export default EntityDetail;
